import apiClient from '../client/axios.config';
import { API_ENDPOINTS } from '../endpoints';

/**
 * Normalizes error messages from API responses.
 */
const extractErrorMessage = (err, fallback = 'Something went wrong. Please try again.') => {
  const data = err?.response?.data || err;

  const fieldErrors = Array.isArray(data?.errors) ? data.errors : null;
  if (fieldErrors && fieldErrors.length > 0) {
    const messages = fieldErrors
      .map((e) => e?.msg || e?.message)
      .filter(Boolean);
    if (messages.length) return messages.join(' ');
  }

  return data?.message || err?.message || fallback;
};

/**
 * inAppNotificationService
 *
 * API service for the bell icon in-app notification system.
 * Talks to sm-api-notificationService at /in-app-notifications via API gateway.
 */
export const inAppNotificationService = {
  /**
   * Fetch paginated in-app notifications
   * Supports both:
   *   getNotifications(recipientId, { isRead?, page?, limit? })
   *   getMyNotifications({ recipientId?, isRead?, page?, limit? })
   */
  getNotifications: async (recipientId, params = {}) => {
    try {
      const queryParams = typeof recipientId === 'string'
        ? { recipientId, ...params }
        : { ...recipientId, ...params };

      const res = await apiClient.get(API_ENDPOINTS.IN_APP_NOTIFICATIONS.LIST, {
        params: queryParams,
      });
      return res.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to fetch notifications. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },

  getMyNotifications: async (params = {}) => {
    try {
      const res = await apiClient.get(API_ENDPOINTS.IN_APP_NOTIFICATIONS.LIST, {
        params,
      });
      return res.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to fetch notifications. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },

  /**
   * Fetch unread notification count
   * Supports both:
   *   getUnreadCount(recipientId)
   *   getUnreadCount({ recipientId? })
   */
  getUnreadCount: async (recipientIdOrParams = {}) => {
    try {
      const params = typeof recipientIdOrParams === 'string'
        ? { recipientId: recipientIdOrParams }
        : recipientIdOrParams;

      const res = await apiClient.get(API_ENDPOINTS.IN_APP_NOTIFICATIONS.COUNT, {
        params,
      });
      return res.data?.data?.count ?? res.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to fetch unread count. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },

  /**
   * Mark a single notification as read
   * @param {string} id - Notification _id
   */
  markAsRead: async (id) => {
    try {
      const res = await apiClient.patch(API_ENDPOINTS.IN_APP_NOTIFICATIONS.MARK_READ(id));
      return res.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to mark notification as read. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },

  /**
   * Mark all notifications as read for a recipient
   * Supports both:
   *   markAllAsRead(recipientId)
   *   markAllAsRead({ recipientId })
   */
  markAllAsRead: async (recipientId) => {
    try {
      const body = typeof recipientId === 'string'
        ? { recipientId }
        : (recipientId && typeof recipientId === 'object' ? recipientId : {});

      const res = await apiClient.patch(API_ENDPOINTS.IN_APP_NOTIFICATIONS.MARK_ALL, body);
      return res.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to mark all notifications as read. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },

  /**
   * Delete a single notification
   * @param {string} id - Notification _id
   */
  deleteNotification: async (id) => {
    try {
      const res = await apiClient.delete(API_ENDPOINTS.IN_APP_NOTIFICATIONS.DELETE(id));
      return res.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to delete notification. Please try again.');
      const normalizedError = new Error(message);
      normalizedError.original = err;
      normalizedError.status = err?.response?.status || err?.status;
      throw normalizedError;
    }
  },
};
